import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../services/api';
import faviconImage from '../assets/favicon.png';

// ── Fonts ─────────────────────────────────────────────────────────────────────
const FONT_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Mono:wght@400;500&display=swap');
  .intel-bebas { font-family: 'Bebas Neue', sans-serif !important; }
  .intel-mono  { font-family: 'DM Mono', monospace !important; }
  @keyframes intel-fadeUp   { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  @keyframes intel-meterFill{ from { width:0!important; } }
  @keyframes spin           { to   { transform:rotate(360deg); } }
  .intel-card  { animation: intel-fadeUp    0.45s ease both; }
  .intel-meter { animation: intel-meterFill 1.1s  cubic-bezier(.4,0,.2,1) both; }
  @media (max-width: 420px) {
    .intel-rpt-header { padding: 10px 12px 10px !important; }
    .intel-rpt-body   { padding: 0 10px !important; }
    .intel-info-item  { flex: 1 1 75px !important; padding: 8px 8px !important; }
    .intel-venue-date { display: none !important; }
    .intel-venue-name { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }
`;

// ── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  bg:'#0C0C0E', surface:'#141416', surface2:'#1C1C20',
  border:'rgba(255,255,255,0.08)', text:'#F0EDE8',
  muted:'#7A7A85', green:'#2ECC71', yellow:'#F1C40F', red:'#E74C3C',
};

const IMPACT_STYLE = {
  HIGH:    { bg:'rgba(39,174,96,0.18)',   color:'#2ECC71', border:'rgba(39,174,96,0.3)'   },
  MEDIUM:  { bg:'rgba(241,196,15,0.14)',  color:'#F1C40F', border:'rgba(241,196,15,0.3)'  },
  WATCH:   { bg:'rgba(231,76,60,0.14)',   color:'#E74C3C', border:'rgba(231,76,60,0.3)'   },
};

// ── Post-match helpers ────────────────────────────────────────────────────────

/**
 * Fuzzy-match a player name to actual scoreboard entries.
 * Works around Cricbuzz abbreviation vs our full-name DB.
 */
function findActual(playerName, list) {
  if (!list?.length) return null;
  const words = n => n.toLowerCase().replace(/[^a-z ]/g,'').split(' ').filter(w => w.length > 1);
  const pw = words(playerName);
  // 1. Exact match
  let hit = list.find(a => a.name.toLowerCase() === playerName.toLowerCase());
  if (hit) return hit;
  // 2. Last-name match
  const lastName = pw[pw.length - 1];
  hit = list.find(a => words(a.name).includes(lastName));
  if (hit) return hit;
  // 3. Any word match (≥4 chars)
  hit = list.find(a => pw.some(w => w.length >= 4 && words(a.name).includes(w)));
  return hit || null;
}

/**
 * Compare a batter's actual runs to the predicted range.
 */
function battingAccuracy(range, actualRuns) {
  if (actualRuns === undefined || actualRuns === null) return null;
  const m = (range || '').match(/(\d+)\s*[–-]\s*(\d+)/);
  if (!m) return null;
  const lo = parseInt(m[1]), hi = parseInt(m[2]);
  const runs = actualRuns;

  if (runs >= lo && runs <= hi)              return { label:'On Point',  color:T.green,  icon:'✓', tip:`Predicted ${lo}–${hi}, scored ${runs}` };
  if (runs > hi && runs <= hi + hi * 0.35)   return { label:'Slight Over', color:T.green,  icon:'↑', tip:`Exceeded by ${runs - hi}` };
  if (runs > hi)                             return { label:'Exceeded',  color:'#5DADE2', icon:'↑↑', tip:`Exceeded by ${runs - hi}` };
  if (runs < lo && runs >= lo - lo * 0.4)   return { label:'Slight Under',color:T.yellow, icon:'↓', tip:`Short by ${lo - runs}` };
  return                                            { label:'Missed',    color:T.red,    icon:'↓↓', tip:`Short by ${lo - runs}` };
}

/**
 * Compare a bowler's actual wickets to the predicted range string "N–M wickets".
 */
function bowlingAccuracy(range, actualWickets) {
  if (actualWickets === undefined) return null;
  const m = (range || '').match(/(\d+)\s*[–-]\s*(\d+)\s*wickets/i);
  if (!m) return null;
  const lo = parseInt(m[1]), hi = parseInt(m[2]);
  const w = actualWickets;
  if (w >= lo && w <= hi)  return { label:'On Point',  color:T.green,  icon:'✓', tip:`Predicted ${lo}–${hi}, got ${w}W` };
  if (w > hi)              return { label:'Exceeded',  color:'#5DADE2', icon:'↑↑', tip:`${w}W vs predicted ${hi}W max` };
  if (w === lo - 1)        return { label:'Close',     color:T.yellow, icon:'↓', tip:`One wicket short` };
  return                          { label:'Missed',    color:T.red,    icon:'↓↓', tip:`Got ${w}W, needed ${lo}+` };
}

/**
 * Overall accuracy score across all matched predictions.
 */
function overallAccuracy(data, postMatch) {
  if (!postMatch?.available || !data) return null;
  const all = [...(data.team1Batters||[]),...(data.team2Batters||[]),...(data.team1Bowlers||[]),...(data.team2Bowlers||[])];
  let matched = 0, onPoint = 0;
  for (const p of all) {
    const isBatter = ['batsman','wicket-keeper'].includes(p.role);
    const act = isBatter
      ? findActual(p.name, postMatch.allBatters)
      : findActual(p.name, postMatch.allBowlers);
    if (!act) continue;
    matched++;
    const acc = isBatter ? battingAccuracy(p.prediction.range, act.runs) : bowlingAccuracy(p.prediction.range, act.wickets);
    if (acc && (acc.label === 'On Point' || acc.label === 'Slight Over' || acc.label === 'Slight Under')) onPoint++;
  }
  if (!matched) return null;
  return { pct: Math.round((onPoint / matched) * 100), matched, onPoint };
}

// ── Toss helpers ──────────────────────────────────────────────────────────────

function deriveToss(toss) {
  if (!toss.winner || !toss.choice) return { battingFirst: null, bowlingFirst: null };
  const other = toss.winner === 'team1' ? 'team2' : 'team1';
  return toss.choice === 'bat'
    ? { battingFirst: toss.winner, bowlingFirst: other }
    : { battingFirst: other,       bowlingFirst: toss.winner };
}

function tossAnalysis(toss, vp) {
  if (!toss.winner || !toss.choice) return null;
  const chaseGood   = vp?.chaseAdvantage;
  const choseToChase = toss.choice === 'bowl';
  const goodCall    = chaseGood ? choseToChase : !choseToChase;
  return {
    goodCall,
    label: goodCall
      ? `Smart call — ${chaseGood ? 'chase-friendly venue' : 'bat-first venue'} supports this decision`
      : `Bold call — ${chaseGood ? 'venue favours chasing' : 'venue favours setting a target'}, going against the data`,
    color: goodCall ? T.green : T.yellow,
  };
}

function adjWinProb(base, toss, vp) {
  if (!toss.winner || !toss.choice) return base;
  const analysis = tossAnalysis(toss, vp);
  if (!analysis?.goodCall) return base;
  const B = 5;
  return toss.winner === 'team1'
    ? { t1: Math.min(72, base.t1 + B), t2: Math.max(28, base.t2 - B) }
    : { t1: Math.max(28, base.t1 - B), t2: Math.min(72, base.t2 + B) };
}

function getTossCtx(player, isBattingFirstTeam, vp) {
  const highDew   = vp?.dewFactor?.toLowerCase().includes('high');
  const spinVenue = (vp?.avgScore || 165) < 162;
  const isBatter  = ['batsman','wicket-keeper'].includes(player.role);
  const isBowler  = player.role === 'bowler';

  if (isBatter) {
    if (isBattingFirstTeam)  return { label:'Batting First · Setting Target', color:T.yellow,  runMult:1.05  };
    if (highDew)             return { label:'Chasing · Dew Boost',            color:T.green,   runMult:1.13  };
    if (spinVenue)           return { label:'Chasing · Tough Spin Track',     color:T.red,     runMult:0.87  };
    return                          { label:'Chasing',                        color:T.muted,   runMult:1.02  };
  }
  if (isBowler) {
    if (isBattingFirstTeam)  return highDew
      ? { label:'Defending · Dew Concern',         color:T.red,    econMult:1.12 }
      : { label:'Defending Target',                color:T.muted,  econMult:1.0  };
    return spinVenue
      ? { label:'Bowling 1st · Spin Conditions',   color:T.green,  econMult:0.88 }
      : { label:'Setting the Tone',                color:T.yellow, econMult:0.95 };
  }
  return isBattingFirstTeam
    ? { label:'Batting First', color:T.yellow, runMult:1.03, econMult:1.05 }
    : { label: highDew ? 'Chasing · Dew Helps':'Chasing', color:highDew?T.green:T.muted, runMult:highDew?1.08:1.0, econMult:0.97 };
}

function applyToss(prediction, tossCtx) {
  if (!tossCtx) return { ...prediction, adjusted: false };
  const pred = { ...prediction, adjusted: false };

  if (tossCtx.runMult && tossCtx.runMult !== 1.0 && pred.range) {
    const m = pred.range.match(/(\d+)\s*[–-]\s*(\d+)/);
    if (m) {
      const lo = Math.max(0,   Math.round(parseInt(m[1]) * tossCtx.runMult));
      const hi = Math.min(110, Math.round(parseInt(m[2]) * tossCtx.runMult));
      pred.range    = `${lo} – ${hi} runs`;
      pred.meterPct = Math.min(95, Math.round((hi / 80) * 100));
      pred.adjusted = true;
    }
  }
  if (tossCtx.econMult && tossCtx.econMult !== 1.0 && pred.range?.includes('Economy')) {
    const m = pred.range.match(/Economy\s*([\d.]+)[–-]([\d.]+)/);
    if (m) {
      const lo = (parseFloat(m[1]) * tossCtx.econMult).toFixed(1);
      const hi = (parseFloat(m[2]) * tossCtx.econMult).toFixed(1);
      pred.range    = pred.range.replace(/Economy\s*[\d.]+-[\d.]+/, `Economy ${lo}–${hi}`);
      pred.adjusted = true;
    }
  }
  return pred;
}

// ── Primitives ────────────────────────────────────────────────────────────────

function Tag({ children, style }) {
  return (
    <span className="intel-mono" style={{
      fontSize:9, letterSpacing:'0.14em', textTransform:'uppercase',
      padding:'3px 10px', borderRadius:2,
      background:T.surface2, border:`1px solid ${T.border}`, color:T.muted, ...style,
    }}>{children}</span>
  );
}

function ImpactBadge({ impact, label }) {
  const s = IMPACT_STYLE[impact] || IMPACT_STYLE.WATCH;
  return (
    <span className="intel-mono" style={{
      fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase',
      padding:'2px 8px', borderRadius:2,
      background:s.bg, color:s.color, border:`1px solid ${s.border}`,
      display:'inline-flex', alignItems:'center', gap:4,
    }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:'currentColor', display:'inline-block' }} />
      {label || impact}
    </span>
  );
}

function Meter({ pct, color }) {
  return (
    <div style={{ height:4, background:T.surface2, borderRadius:2, overflow:'hidden' }}>
      <div className="intel-meter" style={{ height:'100%', width:`${pct}%`, borderRadius:2, background:`linear-gradient(90deg,${color},${color}88)` }} />
    </div>
  );
}

function SectionHeader({ tag, title, color, label, labelColor }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
      <Tag>{tag}</Tag>
      <span className="intel-bebas" style={{ fontSize:22, letterSpacing:'0.06em', color:color||T.text }}>{title}</span>
      {label && (
        <span className="intel-mono" style={{
          fontSize:9, letterSpacing:'0.12em', padding:'3px 10px', borderRadius:2,
          background:`${labelColor||T.yellow}18`, color:labelColor||T.yellow,
          border:`1px solid ${labelColor||T.yellow}44`, textTransform:'uppercase',
        }}>{label}</span>
      )}
    </div>
  );
}

function StatBox({ label, value, sub }) {
  return (
    <div>
      <div className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:15, fontWeight:700, color:T.text, lineHeight:1.2 }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:T.muted }}>{sub}</div>}
    </div>
  );
}

function AccBadge({ acc }) {
  if (!acc) return null;
  return (
    <span className="intel-mono" style={{
      fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase',
      padding:'2px 7px', borderRadius:2,
      background:`${acc.color}18`, color:acc.color, border:`1px solid ${acc.color}44`,
      display:'inline-flex', alignItems:'center', gap:3, flexShrink:0,
    }} title={acc.tip}>
      {acc.icon} {acc.label}
    </span>
  );
}

// ── Match Result Banner ───────────────────────────────────────────────────────

function MatchResultBanner({ postMatch, t1, t2 }) {
  if (!postMatch?.available) return null;
  const { winner, winBy, innings } = postMatch;

  return (
    <div className="intel-card" style={{
      background:T.surface, border:`1px solid ${T.border}`,
      borderLeft:`3px solid ${T.yellow}`, borderRadius:4, padding:'16px 20px', marginBottom:20,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
        <span className="intel-bebas" style={{ fontSize:15, letterSpacing:'0.12em', color:T.yellow }}>
          🏆 MATCH RESULT
        </span>
        {winner && <span style={{ fontSize:13, color:T.text, fontWeight:700 }}>{winner}</span>}
        {winBy  && <span style={{ fontSize:12, color:T.muted }}>won by {winBy}</span>}
      </div>

      {/* Innings scores */}
      {innings?.length > 0 && (
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {innings.map((inn, i) => {
            const isT1 = inn.teamName?.toLowerCase().includes(t1.name?.toLowerCase().split(' ')[0] || t1.short?.toLowerCase())
                      || inn.teamName?.toLowerCase().includes(t1.short?.toLowerCase());
            const teamColor = isT1 ? t1.color : t2.color;
            return (
              <div key={i} style={{
                background:T.surface2, border:`1px solid ${T.border}`,
                borderLeft:`3px solid ${teamColor}`, borderRadius:3,
                padding:'10px 14px', flex:'1 1 160px',
              }}>
                <div className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:4 }}>
                  Innings {i+1} · {inn.teamName || (isT1 ? t1.short : t2.short)}
                </div>
                <div className="intel-bebas" style={{ fontSize:26, color:teamColor, lineHeight:1 }}>
                  {inn.scoreStr}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Accuracy Summary Bar ──────────────────────────────────────────────────────

function AccuracySummary({ data, postMatch }) {
  const acc = useMemo(() => overallAccuracy(data, postMatch), [data, postMatch]);
  if (!acc) return null;

  const color = acc.pct >= 65 ? T.green : acc.pct >= 40 ? T.yellow : T.red;
  return (
    <div style={{
      background:T.surface, border:`1px solid ${T.border}`,
      borderLeft:`3px solid ${color}`, borderRadius:4, padding:'12px 16px', marginBottom:20,
      display:'flex', alignItems:'center', gap:14, flexWrap:'wrap',
    }}>
      <div>
        <div className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:3 }}>
          Prediction Accuracy
        </div>
        <div style={{ display:'flex', alignItems:'baseline', gap:6 }}>
          <span className="intel-bebas" style={{ fontSize:28, color, lineHeight:1 }}>{acc.pct}%</span>
          <span style={{ fontSize:11, color:T.muted }}>{acc.onPoint}/{acc.matched} predictions on point</span>
        </div>
      </div>
      <div style={{ flex:1, minWidth:120 }}>
        <div style={{ height:6, background:T.surface2, borderRadius:3, overflow:'hidden' }}>
          <div className="intel-meter" style={{ height:'100%', width:`${acc.pct}%`, background:`linear-gradient(90deg,${color},${color}88)`, borderRadius:3 }} />
        </div>
      </div>
      <span style={{ fontSize:11, color:T.muted }}>
        {acc.pct >= 65 ? '🎯 Strong model performance' : acc.pct >= 40 ? '📊 Reasonable accuracy' : '🔍 Room to improve'}
      </span>
    </div>
  );
}

// ── Player Card (prediction + optional actual side-by-side) ───────────────────

function PlayerCard({ player, teamColor, delay, tossCtx, isBenched, actual, isLive }) {
  const pred     = applyToss(player.prediction, tossCtx);
  const isBatter = ['batsman','wicket-keeper'].includes(player.role);

  // Accuracy vs actual
  const acc = actual
    ? isBatter
      ? battingAccuracy(pred.range, actual.runs)
      : bowlingAccuracy(pred.range, actual.wickets)
    : null;

  const hasActual = !!actual;

  return (
    <div className="intel-card" style={{
      background:T.surface, borderRadius:4, overflow:'hidden',
      border:`1px solid ${isBenched ? 'rgba(255,255,255,0.04)' : T.border}`,
      borderLeft:`3px solid ${isBenched ? '#2a2a2e' : teamColor}`,
      animationDelay:`${delay}s`,
      opacity: isBenched ? 0.38 : 1,
      transition:'opacity 0.25s',
    }}>

      {/* Header row */}
      <div style={{
        padding:'12px 15px 9px', display:'flex',
        justifyContent:'space-between', alignItems:'flex-start',
        borderBottom:`1px solid ${T.border}`,
      }}>
        <div style={{ flex:1 }}>
          <div className="intel-bebas" style={{ fontSize:19, letterSpacing:'0.04em', lineHeight:1, marginBottom:3, color:isBenched?T.muted:T.text }}>
            {player.name}
          </div>
          <div className="intel-mono" style={{ fontSize:10, color:T.muted, letterSpacing:'0.08em', textTransform:'uppercase' }}>
            {player.role.replace('-',' ')}
          </div>
          <div style={{ marginTop:5, display:'flex', alignItems:'center', gap:5, flexWrap:'wrap' }}>
            {isBenched
              ? <ImpactBadge impact="WATCH" label="BENCHED" />
              : <ImpactBadge impact={player.impact} />
            }
            {tossCtx && !isBenched && (
              <span className="intel-mono" style={{
                fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase',
                padding:'2px 7px', borderRadius:2,
                background:`${tossCtx.color}14`, color:tossCtx.color, border:`1px solid ${tossCtx.color}33`,
              }}>
                {tossCtx.label}
              </span>
            )}
            {isLive && <span className="intel-mono" style={{ fontSize:9, color:T.yellow, animation:'intel-pulse 1.6s ease infinite' }}>● LIVE</span>}
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
          <div className="intel-bebas" style={{
            fontSize:11, letterSpacing:'0.1em', padding:'3px 8px', borderRadius:2,
            background:`${teamColor}22`, color:teamColor, border:`1px solid ${teamColor}44`,
          }}>{player.team}</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, padding:'9px 15px', borderBottom:`1px solid ${T.border}` }}>
        {isBatter ? (
          <>
            <StatBox label="Career SR"   value={player.stats.careerSR||'—'}                                    sub={`${player.stats.careerMatches} m`} />
            <StatBox label="Career Runs" value={player.stats.careerRuns>0 ? player.stats.careerRuns : '—'}     sub={`Avg ${player.stats.careerAvg}`} />
            <StatBox label="Batting Avg" value={player.stats.careerAvg||'—'}                                   sub="per match" />
          </>
        ) : (
          <>
            <StatBox label="Career Wkts" value={player.stats.careerWickets>0 ? player.stats.careerWickets:'—'} sub={`${player.stats.careerMatches} m`} />
            <StatBox label="Economy"     value={player.stats.careerEconomy!=='—' ? player.stats.careerEconomy:'—'} sub="per over" />
            <StatBox label="Wkts/Match"  value={player.stats.wpg||'—'}                                        sub="career avg" />
          </>
        )}
      </div>

      {/* ── Prediction vs Actual block ──────────────────────────────────────── */}
      {hasActual ? (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1px 1fr' }}>

          {/* LEFT: Prediction */}
          <div style={{ padding:'10px 14px' }}>
            <div className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:5 }}>
              Predicted{pred.adjusted && <span style={{ color:T.yellow }}> · adj</span>}
            </div>
            <div style={{ fontSize:12, fontWeight:600, color:T.text, marginBottom:6, lineHeight:1.4 }}>
              {pred.range}
              {pred.srRange && <div style={{ fontSize:10, color:T.muted, fontWeight:400 }}>{pred.srRange}</div>}
            </div>
            <Meter pct={isBenched ? 0 : pred.meterPct} color={teamColor} />
          </div>

          {/* Divider */}
          <div style={{ background:T.border }} />

          {/* RIGHT: Actual */}
          <div style={{ padding:'10px 14px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
              <span className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em' }}>
                Actual
              </span>
              <AccBadge acc={acc} />
            </div>

            {isBatter ? (
              <>
                <div style={{ fontSize:14, fontWeight:700, color:acc?.color||T.text, marginBottom:3 }}>
                  {actual.runs} runs
                  <span style={{ fontSize:11, color:T.muted, fontWeight:400 }}> · {actual.balls}b · SR {actual.sr}</span>
                </div>
                <div style={{ fontSize:10, color:T.muted, marginBottom:5 }}>
                  {actual.fours}×4 &nbsp; {actual.sixes}×6
                  {actual.dismissal && actual.dismissal !== 'not out' && (
                    <span style={{ marginLeft:6, fontStyle:'italic' }}> — {actual.dismissal.slice(0,40)}{actual.dismissal.length>40?'…':''}</span>
                  )}
                  {actual.dismissal === 'not out' && <span style={{ color:T.green, marginLeft:6 }}> not out</span>}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize:14, fontWeight:700, color:acc?.color||T.text, marginBottom:3 }}>
                  {actual.wickets}W / {actual.runs} runs
                  <span style={{ fontSize:11, color:T.muted, fontWeight:400 }}> · {actual.overs} ov</span>
                </div>
                <div style={{ fontSize:10, color:T.muted, marginBottom:5 }}>
                  Economy {actual.economy}
                  {actual.maidens > 0 && <span> · {actual.maidens} maiden{actual.maidens>1?'s':''}</span>}
                </div>
              </>
            )}

            {/* Actual meter */}
            {isBatter && (() => {
              const rangeM = (pred.range||'').match(/(\d+)\s*[–-]\s*(\d+)/);
              const max    = rangeM ? parseInt(rangeM[2]) : 60;
              const pct    = Math.min(100, Math.round((actual.runs / Math.max(max * 1.4, 1)) * 100));
              return <Meter pct={pct} color={acc?.color || teamColor} />;
            })()}
            {!isBatter && (() => {
              const pct = Math.min(100, (actual.wickets / 5) * 100);
              return <Meter pct={pct} color={acc?.color || teamColor} />;
            })()}
          </div>
        </div>
      ) : (
        /* No actual data — normal single prediction block */
        <div style={{ padding:'10px 15px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
            <span className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em' }}>
              {isBatter ? 'Predicted Batting' : 'Predicted Bowling'}
              {pred.adjusted && <span style={{ color:T.yellow, marginLeft:6 }}>· toss adjusted</span>}
            </span>
          </div>
          <div style={{ fontSize:13, fontWeight:600, color:isBenched?T.muted:T.text, marginBottom:6 }}>
            {pred.range}
            {pred.srRange && <span style={{ color:T.muted, fontWeight:400, fontSize:11, marginLeft:8 }}>{pred.srRange}</span>}
          </div>
          <Meter pct={isBenched ? 0 : pred.meterPct} color={teamColor} />
        </div>
      )}
    </div>
  );
}

// ── Battle Card ───────────────────────────────────────────────────────────────

function BattleCard({ battle, t1Color, t2Color, delay }) {
  const VC = { safe:T.green, danger:T.red, neutral:T.yellow };
  const vc = VC[battle.verdict.type] || T.yellow;
  return (
    <div className="intel-card" style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:4, padding:'15px 17px', animationDelay:`${delay}s` }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
        <span className="intel-bebas" style={{ fontSize:17, color:t1Color, letterSpacing:'0.04em' }}>{battle.batter.name}</span>
        <span className="intel-mono"  style={{ fontSize:10, color:T.muted }}>vs</span>
        <span className="intel-bebas" style={{ fontSize:17, color:t2Color, letterSpacing:'0.04em' }}>{battle.bowler.name}</span>
      </div>
      {[
        { label:`${battle.batter.name.split(' ').pop()} Avg`,   val:battle.batter.avg },
        { label:`${battle.batter.name.split(' ').pop()} SR`,    val:battle.batter.sr>0?parseFloat(battle.batter.sr).toFixed(0):'—' },
        { label:`${battle.bowler.name.split(' ').pop()} Wkts/M`,val:battle.bowler.wpg },
        { label:`${battle.bowler.name.split(' ').pop()} Eco`,   val:battle.bowler.eco>0?parseFloat(battle.bowler.eco).toFixed(1):'—' },
        { label:'Verdict',  val:battle.verdict.label, style:{color:vc} },
        { label:'Analysis', val:battle.insight, style:{color:T.muted, fontSize:11, fontStyle:'italic'} },
      ].map((row,i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'4px 0', borderBottom:i<5?`1px solid ${T.border}`:'none', fontSize:12, gap:10 }}>
          <span style={{ color:T.muted, flexShrink:0 }}>{row.label}</span>
          <span style={{ color:T.text, fontWeight:500, textAlign:'right', ...row.style }}>{row.val}</span>
        </div>
      ))}
    </div>
  );
}

// ── H2H Block ─────────────────────────────────────────────────────────────────

function H2HBlock({ h2h, t1Short, t2Short, t1Color, t2Color }) {
  if (!h2h) return null;
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:12 }}>
        {[
          { num:h2h.total, label:'Total Meetings', color:T.text },
          { num:h2h.t1,    label:`${t1Short} Wins`, color:t1Color },
          { num:h2h.t2,    label:`${t2Short} Wins`, color:t2Color },
          { num:h2h.high,  label:'Highest Total',   color:T.yellow, small:true },
        ].map((item,i) => (
          <div key={i} className="intel-card" style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:4, padding:'13px 12px', textAlign:'center', animationDelay:`${i*0.07}s` }}>
            <div className="intel-bebas" style={{ fontSize:item.small?16:28, lineHeight:1, color:item.color, marginBottom:4 }}>{item.num}</div>
            <div className="intel-mono"  style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em' }}>{item.label}</div>
          </div>
        ))}
      </div>
      {h2h.last5?.length>0 && (
        <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:4, padding:'10px 16px', fontSize:12, display:'flex', alignItems:'center', flexWrap:'wrap', gap:6 }}>
          <span style={{ color:T.muted, marginRight:6 }}>Last 5:</span>
          {h2h.last5.map((w,i) => (
            <React.Fragment key={i}>
              <span style={{ color:w===t1Short?t1Color:t2Color, fontWeight:700 }}>{w}</span>
              {i<h2h.last5.length-1 && <span style={{ color:T.border }}>→</span>}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Win Probability Banner ────────────────────────────────────────────────────

function WinProbBanner({ winProb, team1, team2, toss, vp }) {
  const dp = useMemo(() => adjWinProb(winProb, toss, vp), [winProb, toss, vp]);
  const adjusted = dp.t1 !== winProb.t1;
  return (
    <div style={{ marginBottom: adjusted ? 6 : 18 }}>
      <div style={{ display:'flex', background:T.surface, border:`1px solid ${T.border}`, borderRadius:4, overflow:'hidden' }}>
        <div style={{ flex:1, padding:'17px 22px', background:`${team1.color}14`, display:'flex', flexDirection:'column', justifyContent:'space-between' }}>
          <div>
            <div className="intel-bebas" style={{ fontSize:20, color:team1.color, letterSpacing:'0.06em' }}>{team1.name}</div>
            <div style={{ fontSize:11, color:T.muted }}>{team1.short}</div>
          </div>
          <div className="intel-bebas" style={{ fontSize:46, lineHeight:1, color:team1.color }}>{dp.t1}%</div>
        </div>
        <div style={{ width:4, background:T.border }} />
        <div style={{ flex:1, padding:'17px 22px', textAlign:'right', background:`${team2.color}14`, display:'flex', flexDirection:'column', justifyContent:'space-between', alignItems:'flex-end' }}>
          <div>
            <div className="intel-bebas" style={{ fontSize:20, color:team2.color, letterSpacing:'0.06em' }}>{team2.name}</div>
            <div style={{ fontSize:11, color:T.muted }}>{team2.short}</div>
          </div>
          <div className="intel-bebas" style={{ fontSize:46, lineHeight:1, color:team2.color }}>{dp.t2}%</div>
        </div>
      </div>
      {adjusted && (
        <div className="intel-mono" style={{ fontSize:10, color:T.yellow, textAlign:'center', marginTop:5, marginBottom:14, letterSpacing:'0.08em' }}>
          ↑ Adjusted for toss strategy · Base: {winProb.t1}% / {winProb.t2}%
        </div>
      )}
    </div>
  );
}

// ── Toss Panel ────────────────────────────────────────────────────────────────

function TossPanel({ toss, t1, t2, vp }) {
  const { battingFirst, bowlingFirst } = deriveToss(toss);
  const analysis    = toss.winner && toss.choice ? tossAnalysis(toss, vp) : null;
  const battingTeam = battingFirst === 'team1' ? t1 : battingFirst === 'team2' ? t2 : null;
  const bowlingTeam = bowlingFirst === 'team1' ? t1 : bowlingFirst === 'team2' ? t2 : null;
  const hasToss     = !!(toss.winner && toss.choice);

  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderLeft:`3px solid ${T.yellow}`, borderRadius:4, padding:'14px 18px', marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: hasToss ? 12 : 0, flexWrap:'wrap' }}>
        <span className="intel-bebas" style={{ fontSize:15, letterSpacing:'0.12em', color:T.yellow }}>🪙 TOSS UPDATE</span>
        {hasToss ? (
          <span className="intel-mono" style={{ fontSize:9, color:T.green, textTransform:'uppercase', letterSpacing:'0.1em', padding:'2px 7px', borderRadius:2, background:'rgba(46,204,113,0.1)', border:'1px solid rgba(46,204,113,0.25)' }}>
            ✓ Auto-detected from scorecard
          </span>
        ) : (
          <span className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em' }}>
            Awaiting toss data…
          </span>
        )}
      </div>
      {hasToss && (
        <>
          <div style={{ display:'flex', gap:18, flexWrap:'wrap', alignItems:'flex-start' }}>
            {battingTeam && bowlingTeam && (
              <div style={{ flex:1, minWidth:180 }}>
                <div style={{ fontSize:13, fontWeight:600, lineHeight:1.7 }}>
                  <span style={{ color:battingTeam.color }}>{battingTeam.name}</span>
                  <span style={{ color:T.muted }}> bat first</span>
                  <br />
                  <span style={{ color:bowlingTeam.color }}>{bowlingTeam.name}</span>
                  <span style={{ color:T.muted }}> bowl first</span>
                </div>
              </div>
            )}
          </div>
          {analysis && (
            <div style={{ marginTop:12, padding:'7px 12px', borderRadius:3, background:`${analysis.color}10`, border:`1px solid ${analysis.color}33`, fontSize:12, color:analysis.color }}>
              {analysis.goodCall ? '✓ ' : '⚡ '}{analysis.label}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Players grid ──────────────────────────────────────────────────────────────

function PlayersGrid({ players, teamColor, tossInfo, benched, isBattingFirstTeam, vp, postMatch }) {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(310px,1fr))', gap:14, marginBottom:24 }}>
      {players.map((p, i) => {
        const tossCtx   = tossInfo.battingFirst !== null ? getTossCtx(p, isBattingFirstTeam, vp) : null;
        const isBatter  = ['batsman','wicket-keeper'].includes(p.role);
        const actual    = postMatch?.available
          ? isBatter
            ? findActual(p.name, postMatch.allBatters)
            : findActual(p.name, postMatch.allBowlers)
          : null;
        return (
          <PlayerCard
            key={p.name} player={p} teamColor={teamColor}
            delay={i * 0.07} tossCtx={tossCtx}
            isBenched={benched.has(p.name)}
            actual={actual} isLive={postMatch?.status === 'live'}
          />
        );
      })}
    </div>
  );
}

// ── Condition Row ─────────────────────────────────────────────────────────────

function ConditionRow({ label, value, valueStyle }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, padding:'9px 0', borderBottom:`1px solid ${T.border}` }}>
      <span style={{ color:T.muted, fontSize:12, flexShrink:0 }}>{label}</span>
      <span style={{ color:T.text, fontWeight:600, fontSize:13, textAlign:'right', ...valueStyle }}>{value}</span>
    </div>
  );
}

// ── Match List Item ───────────────────────────────────────────────────────────

function MatchListItem({ match, isSelected, onClick, locked, unlockPrice, onLockedClick }) {
  const t1    = match.team1Info || {};
  const t2    = match.team2Info || {};
  const today = new Date().toISOString().split('T')[0];
  const isToday = match.date === today;
  const isDone  = match.status === 'completed';

  const dateLabel = match.date
    ? new Date(match.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short' })
    : '';

  const relTime = (() => {
    if (!match.date || !match.time || isDone) return '';

    const clean = String(match.time).trim();
    const parsed = clean.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!parsed) return '';

    let hh = parseInt(parsed[1], 10);
    const mm = parseInt(parsed[2], 10);
    const ampm = (parsed[3] || '').toUpperCase();
    if (ampm === 'PM' && hh < 12) hh += 12;
    if (ampm === 'AM' && hh === 12) hh = 0;

    const start = new Date(`${match.date}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`);
    const delta = start.getTime() - Date.now();
    if (Number.isNaN(delta)) return '';
    if (delta <= 0) return 'Started';

    const mins = Math.round(delta / 60000);
    if (mins < 60) return `Starts in ${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `Starts in ${h}h ${m}m`;
  })();

  return (
    <div
      onClick={locked ? onLockedClick : onClick}
      style={{
        margin:'8px 10px 0',
        cursor:'pointer',
        borderRadius:10,
        border:`1px solid ${isSelected ? (t1.color || T.border) : T.border}`,
        background: isSelected
          ? `linear-gradient(135deg, ${t1.color}1a 0%, ${T.surface2} 45%, ${t2.color}1a 100%)`
          : T.surface,
        boxShadow: isSelected ? `0 0 0 1px ${t1.color||'#888'}30, 0 4px 16px rgba(0,0,0,0.25)` : 'none',
        transition:'border-color 0.15s, background 0.15s',
        position:'relative',
        overflow:'hidden',
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = (t1.color||'#888') + '55'; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = T.border; }}
    >
      {/* Top strip — match meta */}
      <div style={{
        display:'flex', justifyContent:'space-between', alignItems:'center',
        padding:'6px 12px', borderBottom:`1px solid ${T.border}`,
        background:'rgba(0,0,0,0.18)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          <span className="intel-mono" style={{ fontSize:9, color:T.muted, letterSpacing:'0.08em' }}>M{match.matchNo}</span>
          <span className="intel-mono" style={{ fontSize:9, color: isToday ? T.yellow : T.muted, fontWeight: isToday ? 700 : 400 }}>
            {isToday ? 'TODAY' : dateLabel}
          </span>
          {match.time && <span className="intel-mono" style={{ fontSize:9, color:T.muted }}>{match.time}</span>}
        </div>
        <div style={{ display:'flex', gap:5, alignItems:'center' }}>
          {match.label && <span style={{ fontSize:8, color:T.yellow, fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase' }}>{match.label}</span>}
          {isDone && <span style={{ fontSize:8, color:T.green, fontWeight:700, letterSpacing:'0.06em' }}>DONE</span>}
        </div>
      </div>

      {/* Teams row */}
      <div style={{ display:'flex', alignItems:'center', padding:'12px 12px 10px', gap:6 }}>
        {/* Team 1 */}
        <div style={{ flex:1, textAlign:'left', minWidth:0 }}>
          <div className="intel-bebas" style={{
            fontSize:28, color:t1.color||T.text, lineHeight:1, letterSpacing:'0.04em',
          }}>{match.team1}</div>
          <div style={{
            fontSize:9, color:T.muted, marginTop:3, overflow:'hidden',
            textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.2,
          }}>{t1.name || ''}</div>
        </div>

        {/* VS badge */}
        <div style={{
          flexShrink:0,
          display:'flex', flexDirection:'column', alignItems:'center', gap:2,
          padding:'6px 8px',
          borderRadius:6,
          background:T.surface2,
          border:`1px solid ${T.border}`,
        }}>
          <span className="intel-mono" style={{ fontSize:8, color:T.muted, letterSpacing:'0.1em', lineHeight:1 }}>VS</span>
        </div>

        {/* Team 2 */}
        <div style={{ flex:1, textAlign:'right', minWidth:0 }}>
          <div className="intel-bebas" style={{
            fontSize:28, color:t2.color||T.text, lineHeight:1, letterSpacing:'0.04em',
          }}>{match.team2}</div>
          <div style={{
            fontSize:9, color:T.muted, marginTop:3, overflow:'hidden',
            textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.2,
            direction:'rtl', textAlign:'right',
          }}>{t2.name || ''}</div>
        </div>
      </div>

      {/* Venue */}
      <div style={{
        padding:'0 12px 9px',
        fontSize:10, color:T.muted,
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
        display:'flex', alignItems:'center', gap:4,
      }}>
        <span style={{ flexShrink:0 }}>📍</span>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {match.venue?.split(',').slice(0,2).join(', ') || ''}
        </span>
      </div>

      {relTime && (
        <div style={{
          padding:'0 12px 10px',
          fontSize:9,
          color: relTime === 'Started' ? T.yellow : T.muted,
          fontFamily:"'DM Mono',monospace",
          letterSpacing:'0.08em',
          textTransform:'uppercase',
        }}>
          {relTime}
        </div>
      )}

      {/* Lock overlay */}
      {locked && (
        <div style={{
          position:'absolute', inset:0,
          background:'rgba(8,10,18,0.55)',
          backdropFilter:'blur(2px)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <div style={{
            width:40, height:40, borderRadius:'50%',
            border:`1px solid ${T.yellow}55`,
            background:'rgba(12,12,14,0.88)',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:18,
          }}>🔒</div>
        </div>
      )}
    </div>
  );
}

function LoadingReport() {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:14, padding:40 }}>
      <div style={{ width:44, height:44, borderRadius:'50%', border:`3px solid ${T.border}`, borderTopColor:T.yellow, animation:'spin 0.8s linear infinite' }} />
      <div style={{ color:T.muted, fontSize:13 }}>Generating match intelligence...</div>
    </div>
  );
}

// ── Playing XI Summary ────────────────────────────────────────────────────────

function PlayingXISummary({ data, benched, t1, t2 }) {
  const t1b = [...(data.team1Batters||[]),...(data.team1Bowlers||[])].filter(p => benched.has(p.name)).length;
  const t2b = [...(data.team2Batters||[]),...(data.team2Bowlers||[])].filter(p => benched.has(p.name)).length;
  if (!t1b && !t2b) return null;
  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:4, padding:'10px 16px', marginBottom:18, display:'flex', gap:20, flexWrap:'wrap', alignItems:'center' }}>
      <span className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em' }}>Playing XI</span>
      <span className="intel-mono" style={{ fontSize:9, color:T.green, padding:'2px 7px', borderRadius:2, background:'rgba(46,204,113,0.1)', border:'1px solid rgba(46,204,113,0.25)' }}>✓ Auto-detected from scorecard</span>
      {t1b>0 && <span style={{ fontSize:12 }}><span style={{ color:t1.color, fontWeight:600 }}>{t1.short}</span><span style={{ color:T.muted }}> — {t1b} not played</span></span>}
      {t2b>0 && <span style={{ fontSize:12 }}><span style={{ color:t2.color, fontWeight:600 }}>{t2.short}</span><span style={{ color:T.muted }}> — {t2b} not played</span></span>}
    </div>
  );
}

// ── Full Report ───────────────────────────────────────────────────────────────

function PredictionReport({ data, match, toss, postMatch }) {
  const t1 = data.team1;
  const t2 = data.team2;
  const vp = data.venueProfile;

  // Auto-derive benched set: any tracked player not found in the scorecard is benched.
  // Only active when scorecard data is available (live or completed match).
  const benched = useMemo(() => {
    const result = new Set();
    if (!postMatch?.available) return result;
    const played = [...(postMatch.allBatters || []), ...(postMatch.allBowlers || [])];
    const allTracked = [
      ...(data.team1Batters || []), ...(data.team1Bowlers || []),
      ...(data.team2Batters || []), ...(data.team2Bowlers || []),
    ];
    for (const p of allTracked) {
      if (!findActual(p.name, played)) result.add(p.name);
    }
    return result;
  }, [postMatch, data]);

  const tossInfo        = deriveToss(toss);
  const battingFirst    = tossInfo.battingFirst;
  const bowlingFirst    = tossInfo.bowlingFirst;

  // Reorder helpers
  const firstBatters    = battingFirst === 'team1' ? data.team1Batters : data.team2Batters;
  const secondBatters   = battingFirst === 'team1' ? data.team2Batters : data.team1Batters;
  const firstBatTeam    = battingFirst === 'team1' ? t1 : t2;
  const secondBatTeam   = battingFirst === 'team1' ? t2 : t1;
  const firstBowlers    = bowlingFirst === 'team1' ? data.team1Bowlers : data.team2Bowlers;
  const secondBowlers   = bowlingFirst === 'team1' ? data.team2Bowlers : data.team1Bowlers;
  const firstBowlTeam   = bowlingFirst === 'team1' ? t1 : t2;
  const secondBowlTeam  = bowlingFirst === 'team1' ? t2 : t1;

  const dp = adjWinProb(data.winProbability, toss, vp);

  return (
    <div style={{ color:T.text, fontFamily:"'DM Sans',sans-serif" }}>

      {/* Header */}
      <div className="intel-rpt-header" style={{ padding:'20px 28px 16px', borderBottom:`1px solid ${T.border}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, flexWrap:'wrap' }}>
          <div className="intel-mono" style={{ fontSize:10, color:T.muted, textTransform:'uppercase', letterSpacing:'0.14em', background:T.surface2, border:`1px solid ${T.border}`, padding:'3px 10px', borderRadius:2 }}>
            {data.matchNo?`Match ${data.matchNo} · `:''}Pre-Match Intelligence
          </div>
          {match?.label && <span style={{ fontSize:11, color:T.yellow }}>⚡ {match.label}</span>}
          {postMatch?.available && (
            <span className="intel-mono" style={{ fontSize:9, color:T.green, background:`${T.green}14`, border:`1px solid ${T.green}44`, padding:'3px 9px', borderRadius:2, textTransform:'uppercase', letterSpacing:'0.1em' }}>
              ✓ Post-Match Data Available
            </span>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
          <div className="intel-bebas" style={{ fontSize:'clamp(28px,4vw,50px)', letterSpacing:'0.02em', lineHeight:1, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ color:t1.color }}>{t1.short}</span>
            <span style={{ color:T.muted, fontSize:'0.5em' }}>VS</span>
            <span style={{ color:t2.color }}>{t2.short}</span>
          </div>
          <div style={{ textAlign:'right', color:T.muted, fontSize:12, lineHeight:1.9, minWidth:0 }}>
            <div className="intel-venue-name" style={{ color:T.text, fontWeight:600, fontSize:13 }}>{data.venue?.split(',').slice(0,2).join(',')||'Venue TBD'}</div>
            {data.matchDate && <div className="intel-venue-date">{new Date(data.matchDate+'T00:00:00').toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>}
            {vp && <div style={{ color:T.text }}>Avg: {vp.avgScore} · {vp.pitchType}</div>}
          </div>
        </div>
      </div>

      {/* Info bar */}
      <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, flexWrap:'wrap' }}>
        {[
          { label:'Pitch',       value:vp?.pitchType||'—' },
          { label:'Dew Factor',  value:vp?.dewFactor||'—' },
          { label:'Toss Edge',   value:data.tossAdvantage||'—' },
          { label:'Boundaries',  value:vp?.boundarySize||'—' },
        ].map((item,i) => (
          <div key={i} className="intel-info-item" style={{ padding:'10px 16px', borderRight:`1px solid ${T.border}`, flex:'1 1 75px' }}>
            <div className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:2 }}>{item.label}</div>
            <div style={{ fontSize:12, fontWeight:500 }}>{item.value}</div>
          </div>
        ))}
      </div>

      <div className="intel-rpt-body" style={{ padding:'0 28px' }}>

        <div style={{ paddingTop:20 }}>

          {/* Demo data warning (staging mode only) */}
          {postMatch?._isDemo && (
            <div style={{ background:'rgba(241,196,15,0.10)', border:'1px solid rgba(241,196,15,0.28)', borderRadius:4, padding:'8px 14px', marginBottom:14, fontSize:10, color:'#f1c40f', fontFamily:"'DM Mono',monospace", letterSpacing:'0.08em', textTransform:'uppercase' }}>
              ⚠ Demo Data — Staging Mode · Not real match data
            </div>
          )}

          {/* Match Result Banner (post-match only) */}
          <MatchResultBanner postMatch={postMatch} t1={t1} t2={t2} />

          {/* Accuracy Summary (post-match only) */}
          {postMatch?.available && <AccuracySummary data={data} postMatch={postMatch} />}

          {/* Toss panel */}
          <TossPanel toss={toss} t1={t1} t2={t2} vp={vp} />

          {/* Win Probability */}
          <SectionHeader tag="Analysis" title="Win Probability" />
          <WinProbBanner winProb={data.winProbability} team1={t1} team2={t2} toss={toss} vp={vp} />

          {/* Projected scores */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:22 }}>
            {[
              { team:t1, score:data.projectedScores?.team1 },
              { team:t2, score:data.projectedScores?.team2 },
            ].map(({team,score},i) => (
              <div key={i} style={{ background:T.surface, border:`1px solid ${T.border}`, borderLeft:`3px solid ${team.color}`, borderRadius:4, padding:'12px 16px' }}>
                <div className="intel-mono" style={{ fontSize:9, color:T.muted, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:5 }}>{team.name} Projected</div>
                <div className="intel-bebas" style={{ fontSize:24, color:team.color }}>{score?`${score.min} – ${score.max}`:'—'}</div>
              </div>
            ))}
          </div>

          {/* H2H */}
          <SectionHeader tag="Historical" title="Head-to-Head" />
          <H2HBlock h2h={data.h2h} t1Short={t1.short} t2Short={t2.short} t1Color={t1.color} t2Color={t2.color} />

          {/* Playing XI summary */}
          <PlayingXISummary data={data} benched={benched} t1={t1} t2={t2} />

        </div>

        {/* ── Player sections ── */}
        {battingFirst ? (
          <>
            {firstBatters?.length>0 && (
              <div>
                <SectionHeader tag={firstBatTeam.short} title={`Batting — ${firstBatTeam.name}`} color={firstBatTeam.color} label="Batting First" labelColor={firstBatTeam.color} />
                <PlayersGrid players={firstBatters} teamColor={firstBatTeam.color} tossInfo={tossInfo} benched={benched}isBattingFirstTeam={true} vp={vp} postMatch={postMatch} />
              </div>
            )}
            {firstBowlers?.length>0 && (
              <div>
                <SectionHeader tag={firstBowlTeam.short} title={`Bowling — ${firstBowlTeam.name}`} color={firstBowlTeam.color} label="Bowling First" labelColor={firstBowlTeam.color} />
                <PlayersGrid players={firstBowlers} teamColor={firstBowlTeam.color} tossInfo={tossInfo} benched={benched}isBattingFirstTeam={false} vp={vp} postMatch={postMatch} />
              </div>
            )}
            {data.battles?.length>0 && (
              <div>
                <SectionHeader tag="Matchups" title="⚡ Key Battles" />
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(310px,1fr))', gap:14, marginBottom:24 }}>
                  {data.battles.map((b,i) => <BattleCard key={i} battle={b} t1Color={t1.color} t2Color={t2.color} delay={i*0.09} />)}
                </div>
              </div>
            )}
            {secondBatters?.length>0 && (
              <div>
                <SectionHeader tag={secondBatTeam.short} title={`Batting — ${secondBatTeam.name}`} color={secondBatTeam.color} label="Will Chase" labelColor="#3b82f6" />
                <PlayersGrid players={secondBatters} teamColor={secondBatTeam.color} tossInfo={tossInfo} benched={benched}isBattingFirstTeam={false} vp={vp} postMatch={postMatch} />
              </div>
            )}
            {secondBowlers?.length>0 && (
              <div>
                <SectionHeader tag={secondBowlTeam.short} title={`Bowling — ${secondBowlTeam.name}`} color={secondBowlTeam.color} label="Will Defend" labelColor={T.muted} />
                <PlayersGrid players={secondBowlers} teamColor={secondBowlTeam.color} tossInfo={tossInfo} benched={benched}isBattingFirstTeam={true} vp={vp} postMatch={postMatch} />
              </div>
            )}
          </>
        ) : (
          <>
            {data.team1Batters?.length>0 && (
              <div>
                <SectionHeader tag={t1.short} title={`Batting — ${t1.name}`} color={t1.color} />
                <PlayersGrid players={data.team1Batters} teamColor={t1.color} tossInfo={tossInfo} benched={benched}isBattingFirstTeam={false} vp={vp} postMatch={postMatch} />
              </div>
            )}
            {data.team2Batters?.length>0 && (
              <div>
                <SectionHeader tag={t2.short} title={`Batting — ${t2.name}`} color={t2.color} />
                <PlayersGrid players={data.team2Batters} teamColor={t2.color} tossInfo={tossInfo} benched={benched}isBattingFirstTeam={false} vp={vp} postMatch={postMatch} />
              </div>
            )}
            {(data.team1Bowlers?.length>0||data.team2Bowlers?.length>0) && (
              <div>
                <SectionHeader tag="Bowling" title="Key Bowling Threats" />
                <PlayersGrid
                  players={[...(data.team1Bowlers||[]).slice(0,2),...(data.team2Bowlers||[]).slice(0,2)]}
                  teamColor={t1.color} tossInfo={tossInfo} benched={benched}
                  isBattingFirstTeam={false} vp={vp} postMatch={postMatch}
                />
              </div>
            )}
            {data.battles?.length>0 && (
              <div>
                <SectionHeader tag="Matchups" title="⚡ Key Battles to Watch" />
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(310px,1fr))', gap:14, marginBottom:24 }}>
                  {data.battles.map((b,i) => <BattleCard key={i} battle={b} t1Color={t1.color} t2Color={t2.color} delay={i*0.09} />)}
                </div>
              </div>
            )}
          </>
        )}

        {/* Conditions */}
        <SectionHeader tag="Conditions" title="Pitch & Ground Analysis" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:14, marginBottom:24 }}>
          {/* Pitch Conditions card */}
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderTop:`3px solid #3b82f6`, borderRadius:6, padding:'16px 18px', minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
              <span style={{ fontSize:16 }}>🏏</span>
              <span className="intel-bebas" style={{ fontSize:15, letterSpacing:'0.1em', color:'#3b82f6' }}>PITCH CONDITIONS</span>
            </div>
            <ConditionRow label="Pitch Type"         value={vp?.pitchType||'—'} />
            <ConditionRow label="Avg 1st Innings"    value={`${vp?.avgScore||'—'} runs`} />
            <ConditionRow label="Spin Effectiveness" value={vp?.spinEffectiveness||'—'} />
            <ConditionRow label="Pace Effectiveness" value={vp?.paceEffectiveness||'—'} />
            <ConditionRow label="Boundaries"         value={vp?.boundarySize||'—'} />
            {vp?.notes && (
              <div style={{ marginTop:12, fontSize:11, color:T.muted, lineHeight:1.7, fontStyle:'italic', padding:'8px 10px', background:T.surface2, borderRadius:4 }}>
                {vp.notes}
              </div>
            )}
          </div>

          {/* Match Factors card */}
          <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderTop:`3px solid ${T.yellow}`, borderRadius:6, padding:'16px 18px', minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
              <span style={{ fontSize:16 }}>⚡</span>
              <span className="intel-bebas" style={{ fontSize:15, letterSpacing:'0.1em', color:T.yellow }}>MATCH FACTORS</span>
            </div>
            <ConditionRow label="Dew Factor"     value={vp?.dewFactor||'—'}  valueStyle={{color:T.yellow}} />
            <ConditionRow label="Toss Advantage" value={data.tossAdvantage||'—'} />
            <ConditionRow label="Chase Advantage" value={vp?.chaseAdvantage?'Yes — conditions help':'Neutral / Bat-first'} />
            <ConditionRow label={`${t1.short} Projected`} value={data.projectedScores?.team1?`${data.projectedScores.team1.min}–${data.projectedScores.team1.max} runs`:'—'} valueStyle={{color:t1.color}} />
            <ConditionRow label={`${t2.short} Projected`} value={data.projectedScores?.team2?`${data.projectedScores.team2.min}–${data.projectedScores.team2.max} runs`:'—'} valueStyle={{color:t2.color}} />
          </div>
        </div>

        {/* Final verdict */}
        <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderLeft:`3px solid ${T.yellow}`, borderRadius:4, padding:'15px 20px', marginBottom:30 }}>
          <div className="intel-bebas" style={{ fontSize:14, letterSpacing:'0.12em', color:T.yellow, marginBottom:8 }}>⚡ FINAL PREDICTION</div>
          {battingFirst && (
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginBottom:8, background:T.surface2, border:`1px solid ${T.border}`, borderRadius:3, padding:'5px 12px', fontSize:12 }}>
              <span style={{ color:(battingFirst==='team1'?t1:t2).color, fontWeight:700 }}>{(battingFirst==='team1'?t1:t2).name}</span>
              <span style={{ color:T.muted }}>bat first ·</span>
              <span style={{ color:(bowlingFirst==='team1'?t1:t2).color, fontWeight:700 }}>{(bowlingFirst==='team1'?t1:t2).name}</span>
              <span style={{ color:T.muted }}>bowl first</span>
            </div>
          )}
          <div style={{ fontSize:13, lineHeight:1.8 }}>
            <strong style={{ color:t1.color }}>{t1.name}</strong>{' '}
            {dp.t1>=dp.t2?`hold a ${dp.t1}–${dp.t2} advantage`:`are underdogs at ${dp.t1}–${dp.t2}`}.
          </div>
          <div style={{ marginTop:10, display:'flex', gap:18, flexWrap:'wrap' }}>
            <div style={{ fontSize:12, color:T.muted }}>
              🏆 Predicted: <strong style={{ color:dp.t1>=dp.t2?t1.color:t2.color }}>{dp.t1>=dp.t2?t1.name:t2.name}</strong>
            </div>
            {postMatch?.winner && (
              <div style={{ fontSize:12, color:T.muted }}>
                ✅ Actual: <strong style={{ color:T.green }}>{postMatch.winner}</strong>
                {postMatch.winBy && <span> · {postMatch.winBy}</span>}
              </div>
            )}
            <div style={{ fontSize:12, color:T.muted }}>📊 Generated: <span style={{ color:T.text }}>{new Date(data.generatedAt).toLocaleTimeString('en-IN')}</span></div>
          </div>
        </div>
      </div>

      <div style={{ padding:'13px 28px', borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <div style={{ fontSize:11, color:T.muted, maxWidth:480, lineHeight:1.5 }}>
          ⚠️ Predictions use career stats, H2H &amp; venue data. Toss adjustments apply dew/pitch multipliers. Accuracy % reflects predicted-vs-actual ranges.
        </div>
        <div className="intel-mono" style={{ fontSize:10, color:T.muted, letterSpacing:'0.1em' }}>IPL INTELLIGENCE · {data.matchDate||'IPL 2026'}</div>
      </div>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:14, padding:40, textAlign:'center' }}>
      <div style={{ fontSize:36 }}>🏏</div>
      <div className="intel-bebas" style={{ fontSize:21, letterSpacing:'0.06em', color:T.muted }}>SELECT A MATCH</div>
      <div style={{ fontSize:13, color:T.muted, maxWidth:270, lineHeight:1.6 }}>Pick a match from the schedule to generate the intelligence report.</div>
    </div>
  );
}

// ── Analytics Paywall Modal ───────────────────────────────────────────────────

function IntelPaywallModal({ data, onClose, onUnlocked }) {
  const [loading, setLoading] = useState(false);
  const [errMsg,  setErrMsg]  = useState('');

  const handleUnlock = async () => {
    setLoading(true);
    setErrMsg('');
    try {
      const r = await API.post('/intelligence/unlock/order');
      const { order_id, amount, currency, key } = r.data;

      // Lazy-load Razorpay script
      await new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve();
        const s = document.createElement('script');
        s.src = 'https://checkout.razorpay.com/v1/checkout.js';
        s.onload = resolve;
        s.onerror = reject;
        document.body.appendChild(s);
      });

      const rzp = new window.Razorpay({
        key, amount, currency, order_id,
        name:        'IPLTrade',
        description: `Analytics Unlock — IPL ${data.session_year}`,
        image:       window.location.origin + faviconImage,
        handler: async (response) => {
          try {
            await API.post('/intelligence/unlock/verify', {
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            });
            onUnlocked();
          } catch {
            setErrMsg('Payment recorded — please refresh and try again.');
          }
        },
        theme:  { color: '#F1C40F' },
        modal:  { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (err) {
      setErrMsg(err.response?.data?.error || 'Payment setup failed');
      setLoading(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:12, padding:'1.5rem', width:'100%', maxWidth:340 }}>
        <div style={{ textAlign:'center', marginBottom:20 }}>
          <div style={{ fontSize:36, marginBottom:8 }}>🔒</div>
          <div className="intel-bebas" style={{ fontSize:22, letterSpacing:'0.12em', color:T.yellow }}>ANALYTICS LOCKED</div>
          <div style={{ color:T.muted, fontSize:13, marginTop:8, lineHeight:1.6 }}>
            {data.message}
          </div>
        </div>

        <div style={{ background:T.surface2, borderRadius:8, padding:'14px 16px', marginBottom:16, textAlign:'center', border:`1px solid ${T.border}` }}>
          <div style={{ color:T.muted, fontSize:11, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.1em' }}>Unlock Full Season</div>
          <div className="intel-bebas" style={{ fontSize:40, color:T.green, letterSpacing:'0.04em', lineHeight:1 }}>₹{data.unlock_price}</div>
          <div style={{ color:T.muted, fontSize:11, marginTop:4 }}>All remaining matches · IPL {data.session_year}</div>
        </div>

        {errMsg && <div style={{ color:T.red, fontSize:11, marginBottom:10, textAlign:'center' }}>{errMsg}</div>}

        <button
          onClick={handleUnlock}
          disabled={loading}
          style={{ width:'100%', padding:'12px', borderRadius:6, border:'none', background:T.yellow, color:'#000', fontWeight:700, fontSize:14, cursor:loading?'not-allowed':'pointer', opacity:loading?0.7:1, marginBottom:8, letterSpacing:'0.02em' }}
        >
          {loading ? 'Opening payment...' : `Unlock for ₹${data.unlock_price}`}
        </button>
        <button
          onClick={onClose}
          style={{ width:'100%', padding:'10px', borderRadius:6, border:`1px solid ${T.border}`, background:'transparent', color:T.muted, fontSize:12, cursor:'pointer' }}
        >
          Maybe Later
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function IntelligencePage() {
  const [matches,         setMatches]         = useState([]);
  const [selected,        setSelected]        = useState(null);
  const [report,          setReport]          = useState(null);
  const [postMatch,       setPostMatch]       = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [error,           setError]           = useState('');
  const [filter,          setFilter]          = useState('all');
  const [toss,            setToss]            = useState({ winner:'', choice:'' });

  // Mobile: single-column layout with view toggle
  const [isMobile,    setIsMobile]    = useState(window.innerWidth <= 768);
  const [mobileView,  setMobileView]  = useState('list'); // 'list' | 'detail'

  // Session selector: year extracted from match dates
  const [selectedYear, setSelectedYear] = useState(null);

  // Analytics paywall
  const [intelAccess,  setIntelAccess]  = useState(null);  // { paid, views_remaining, … }
  const [paywallData,  setPaywallData]  = useState(null);  // set on 402 response
  const [pendingMatch, setPendingMatch] = useState(null);  // match to retry after unlock
  const [, setClockTick] = useState(0);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setClockTick(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch access status (paywall quota)
  useEffect(() => {
    API.get('/intelligence/access').then(r => setIntelAccess(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/intelligence/schedule')
      .then(r => {
        const ms = r.data.matches || [];
        setMatches(ms);
        // Default to the latest year found in the schedule
        const years = [...new Set(ms.map(m => {
          const y = String(m.date || '').match(/\d{4}/);
          return y ? parseInt(y[0]) : null;
        }).filter(Boolean))].sort((a,b) => b-a);
        if (years.length) setSelectedYear(years[0]);
      })
      .catch(() => setMatches([]))
      .finally(() => setScheduleLoading(false));
  }, []);

  const isMatchLocked = useCallback((match) => {
    if (!intelAccess || intelAccess.paid || intelAccess.free_limit === 0) return false;
    if (match?.status === 'completed') return false;
    return (match.matchNo || 0) > (intelAccess.free_limit || 0);
  }, [intelAccess]);

  const selectMatch = useCallback(match => {
    setSelected(match);
    setReport(null);
    setPostMatch(null);
    setError('');
    setLoading(true);
    setToss({ winner:'', choice:'' });
    if (isMobile) setMobileView('detail');

    const params = new URLSearchParams({ team1: match.team1, team2: match.team2 });
    if (match.venue)   params.set('venue',   match.venue);
    if (match.matchNo) params.set('matchNo', match.matchNo);
    if (match.date)    params.set('date',    match.date);

    Promise.all([
      API.get(`/intelligence/predict?${params}`),
      API.get(`/intelligence/postmatch?team1=${match.team1}&team2=${match.team2}${match.date?`&date=${match.date}`:''}`),
    ]).then(([predRes, postRes]) => {
      if (predRes.data?.pending) {
        setError(predRes.data.reason || 'Teams not yet decided for this fixture.');
        return;
      }
      setReport(predRes.data);
      setPostMatch(postRes.data);
      if (postRes.data?.toss) setToss(postRes.data.toss);
      // Refresh access count after a successful view
      API.get('/intelligence/access').then(r => setIntelAccess(r.data)).catch(() => {});
    }).catch(e => {
      if (e.response?.status === 402) {
        setPendingMatch(match);
        setPaywallData(e.response.data);
        return;
      }
      if (e.response?.status === 401) {
        setError('Please log in to view match analytics.');
        return;
      }
      setError(e.response?.data?.error || 'Failed to load prediction');
    }).finally(() => setLoading(false));
  }, [isMobile]);

  const openPaywall = useCallback((match) => {
    if (!intelAccess) return;
    setPendingMatch(match);
    setPaywallData({
      error: 'paywall',
      message: `Only the first ${intelAccess.free_limit} matches are free in IPL ${intelAccess.session_year}. Unlock the full season to view Match ${match.matchNo} and all remaining analytics.`,
      free_limit: intelAccess.free_limit,
      unlock_price: intelAccess.unlock_price,
      session_year: intelAccess.session_year,
      locked_from_match_no: intelAccess.locked_from_match_no,
      requested_match_no: match.matchNo,
    });
  }, [intelAccess]);

  // Available years from match dates
  const availableYears = useMemo(() => {
    const years = [...new Set(matches.map(m => {
      const y = String(m.date || '').match(/\d{4}/);
      return y ? parseInt(y[0]) : null;
    }).filter(Boolean))].sort((a,b) => b-a);
    return years;
  }, [matches]);

  const filtered = useMemo(() => matches.filter(m => {
    const matchYear = (() => {
      const y = String(m.date || '').match(/\d{4}/);
      return y ? parseInt(y[0]) : null;
    })();
    if (selectedYear && matchYear && matchYear !== selectedYear) return false;
    if (filter === 'upcoming')  return m.status !== 'completed';
    if (filter === 'completed') return m.status === 'completed';
    return true;
  }), [matches, filter, selectedYear]);

  const schedulePanel = (
    <div style={{
      width: isMobile ? '100%' : 288,
      flexShrink: 0,
      borderRight: isMobile ? 'none' : `1px solid ${T.border}`,
      display:'flex', flexDirection:'column',
      overflowY:'auto',
      flex: isMobile ? 1 : undefined,
    }}>
      <div style={{ padding:'13px 13px 11px', borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
        {/* Session year selector */}
        {availableYears.length > 0 && (
          <div style={{ marginBottom:8 }}>
            <select
              value={selectedYear || ''}
              onChange={e => setSelectedYear(parseInt(e.target.value) || null)}
              style={{
                width:'100%', padding:'4px 8px', fontSize:11, borderRadius:3,
                background:T.surface2, border:`1px solid ${T.border}`, color:T.text,
                cursor:'pointer', fontFamily:"'DM Mono',monospace",
              }}
            >
              {availableYears.map(yr => (
                <option key={yr} value={yr}>IPL {yr}{yr === new Date().getFullYear() ? ' (Current)' : ''}</option>
              ))}
            </select>
          </div>
        )}
        <div className="intel-bebas" style={{ fontSize:20, letterSpacing:'0.05em', marginBottom:12 }}>
          IPL {selectedYear || new Date().getFullYear()} SCHEDULE
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {['all','upcoming','completed'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              flex:1, padding:'9px 0', fontSize:10, borderRadius:2,
              border:`1px solid ${filter===f?'#F1C40F44':T.border}`,
              background:filter===f?'rgba(241,196,15,0.1)':'transparent',
              color:filter===f?T.yellow:T.muted, cursor:'pointer',
              textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'DM Mono',monospace",
            }}>{f}</button>
          ))}
        </div>
      </div>
      {scheduleLoading
        ? <div style={{ padding:18, color:T.muted, fontSize:12, textAlign:'center' }}>Loading...</div>
        : filtered.length===0
          ? <div style={{ padding:18, color:T.muted, fontSize:12, textAlign:'center' }}>No matches</div>
          : <div style={{ paddingBottom:10 }}>{filtered.map(m => (
              <MatchListItem
                key={`${m.matchNo}-${m.team1}-${m.team2}`}
                match={m}
                isSelected={selected?.matchNo===m.matchNo && selected?.team1===m.team1}
                locked={isMatchLocked(m)}
                unlockPrice={intelAccess?.unlock_price || 25}
                onLockedClick={() => openPaywall(m)}
                onClick={() => selectMatch(m)}
              />
            ))}</div>
      }
    </div>
  );

  const detailPanel = (
    <div style={{ flex:1, overflowY:'auto', position:'relative' }}>
      {/* Mobile back button */}
      {isMobile && mobileView==='detail' && (
        <button
          onClick={() => setMobileView('list')}
          style={{
            display:'flex', alignItems:'center', gap:6,
            padding:'10px 16px', background:T.surface, border:'none',
            borderBottom:`1px solid ${T.border}`, width:'100%', cursor:'pointer',
            color:T.yellow, fontSize:12, fontFamily:"'DM Mono',monospace",
            letterSpacing:'0.06em', textTransform:'uppercase',
          }}
        >
          ← Back to Matches
        </button>
      )}
      {loading && <LoadingReport />}
      {!loading && error && <div style={{ padding:30, color:T.red, fontSize:13 }}>⚠️ {error}</div>}
      {!loading && !error && report && (
        <PredictionReport data={report} match={selected} toss={toss} postMatch={postMatch} />
      )}
      {!loading && !error && !report && <EmptyState />}
    </div>
  );

  const handleUnlocked = useCallback(() => {
    setPaywallData(null);
    API.get('/intelligence/access').then(r => setIntelAccess(r.data)).catch(() => {});
    if (pendingMatch) {
      setPendingMatch(null);
      selectMatch(pendingMatch);
    }
  }, [pendingMatch, selectMatch]);

  return (
    <>
      <style>{FONT_STYLE}</style>
      <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', height:'calc(100vh - 56px)', background:T.bg, color:T.text, overflow:'hidden' }}>
        {/* On mobile show only one panel at a time */}
        {(!isMobile || mobileView === 'list')   && schedulePanel}
        {(!isMobile || mobileView === 'detail') && detailPanel}
      </div>
      {paywallData && (
        <IntelPaywallModal
          data={paywallData}
          onClose={() => setPaywallData(null)}
          onUnlocked={handleUnlocked}
        />
      )}
    </>
  );
}


